import { BaseChartProps } from "@/shared/types/charts";
import { useTheme } from "@mui/material";
import { EChartsOption, LineSeriesOption } from "echarts";
import ReactECharts from "echarts-for-react";

type LineChartProps = BaseChartProps & {
  series: LineSeriesOption[];
  axisLabels: string[];
};

/**
 * Example usage of LineChart component:
 *
 * const exampleSeries: LineSeriesOption[] = [
 *   {
 *     name: "Revenue",
 *     type: "line",
 *     data: [100, 200, 150, 300, 250],
 *     color: "#1976d2",
 *   },
 *   {
 *     name: "Profit",
 *     type: "line",
 *     data: [50, 100, 75, 150, 125],
 *     color: "#dc004e",
 *     // Dashed line style example:
 *     lineStyle: {
 *       type: "dashed",
 *     },
 *   },
 * ];
 *
 * <LineChart
 *   axisLabels={["Jan", "Feb", "Mar", "Apr", "May"]}
 *   series={exampleSeries}
 *   yAxisLabel="Amount ($)"
 *   yAxisLabelFormatter={(value) => `$${value}K`}
 * />
 */

export const LineChart = ({
  axisLabels,
  series,
  xAxisLabelFormatter,
  yAxisLabel,
  yAxisLabelFormatter,
  tooltipOptions,
  overrides = {},
}: LineChartProps) => {
  const theme = useTheme();
  const tooltipPalette = theme.palette.echartTooltip ?? {};
  const textColorPalette = theme.palette.brandedTypography ?? {};
  const guideLinesPalette = theme.palette.ChartGuideLines ?? {};

  const baseOption: EChartsOption = {
    grid: {
      left: 8,
      right: 20,
      top: 40,
      bottom: 20,
      containLabel: true,
    },
    tooltip: {
      trigger: "axis",
      backgroundColor: tooltipPalette.background || theme.palette.background?.paper || "#fff",
      textStyle: {
        color: tooltipPalette.textColor || theme.palette.text?.primary,
      },
      ...(tooltipOptions ?? {}),
    },
    xAxis: {
      data: axisLabels,
      axisLabel: {
        color: textColorPalette.textColor || theme.palette.text?.primary,
        fontSize: 14,
        formatter: xAxisLabelFormatter,
        padding: [12, 0, 0, 0],
      },
    },
    yAxis: {
      ...(yAxisLabel && {
        name: yAxisLabel,
        nameTextStyle: {
          color: textColorPalette.textColor || theme.palette.text?.primary,
          fontWeight: 600,
          fontSize: "14px",
          padding: [0, 0, 10, -40],
        },
      }),
      axisLabel: {
        fontSize: 14,
        color: textColorPalette.textColor || theme.palette.text?.primary,
        formatter: yAxisLabelFormatter,
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: guideLinesPalette.color || theme.palette.divider,
        },
      },
    },
    series,
  };

  const option: EChartsOption = {
    ...baseOption,
    ...overrides,
  };

  return (
    <ReactECharts option={option} style={{ minHeight: "500px", width: "100%" }} notMerge={true} />
  );
};

export default LineChart;
